import { Component, OnInit, AfterViewInit, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { RoutingService } from "../../providers/routing.service";
import { AuthService } from "../../providers/auth.service";
import { SubscriptionService } from "../../providers/subscription.service";
import { ProfileService } from "../../providers/profile.service";
import { ConfigService } from "../../providers/config.service";
import { UtilService } from "../../providers/util.service";
import { ApiService } from "../../providers/api.service";
import { PricingCatalogService } from "../../providers/pricing-catalog.service";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { environment } from "../../../environments/environment";
import * as math from "mathjs/dist/math";
import { LoadingOverlayComponent } from "../../shared/loading-overlay/loading-overlay.component";
import { BvDialogTitleComponent, BvDialogBodyComponent, BvDialogFooterComponent } from "../../design-system/primitives/dialog.component";
import { CurrencyPipe } from "@angular/common";
import { BvAlertComponent } from "../../design-system/primitives/alert.component";
import { CreditCardComponent } from "../credit-card/credit-card.component";
import { BvInputDirective } from "../../design-system/primitives/input.directive";
import { FormsModule } from "@angular/forms";
import { BvButtonComponent } from "../../design-system/primitives/button.component";
import { BvCheckboxComponent } from "../../design-system/primitives/checkbox.component";
import { CloseDialogButtonComponent } from "../../shared/close-dialog-button/close-dialog-button.component";

interface CatalogTierResponse {
  currency: string;
  monthly: number;
  annual: number;
}
interface CatalogResponse {
  countryCode: string;
  currency: string;
  tiers: { [productCode: string]: CatalogTierResponse };
}
interface ConversionPreview {
  refund: { amount: number; currency: string };
  newCharge: { amount: number; currency: string; priceId: string };
}

@Component({
    selector: "app-checkout",
    templateUrl: "./checkout.component.html",
    styleUrls: ["./checkout.component.scss"],
    imports: [LoadingOverlayComponent, BvDialogTitleComponent, BvDialogBodyComponent, BvAlertComponent, CreditCardComponent, BvInputDirective, FormsModule, BvButtonComponent, BvCheckboxComponent, BvDialogFooterComponent, CloseDialogButtonComponent, CurrencyPipe, TranslateModule]
})
export class CheckoutComponent implements OnInit, AfterViewInit, OnDestroy {
  plan: any = {};
  tax: number = 0;
  subTotal: number = 0;
  credit: number = 0;
  discount: number = 0;
  remaining: number = 0;
  couponID!: string;
  coupon: any;
  acctBal: number = 0;
  amountDue: number = 0;
  total: number = 0;
  catalog: CatalogResponse | null = null;
  isCurrencyChange: boolean = false;
  conversionPreview: ConversionPreview | null = null;
  termsAccepted: boolean = false;
  termsUrl: string = `${environment.externalHomeUrl}/terms`;
  privacyUrl: string = `${environment.externalHomeUrl}/privacy`;
  constructor(
    public route: ActivatedRoute,
    public routing: RoutingService,
    public auth: AuthService,
    public subServ: SubscriptionService,
    public profServ: ProfileService,
    public configServ: ConfigService,
    private util: UtilService,
    private api: ApiService,
    private pricingCatalog: PricingCatalogService,
    private translate: TranslateService
  ) {}

  // Show a heads-up when the user looks Russian by either signal: their
  // locked-in profile country or the current UI language. Stripe stopped
  // serving Russia in 2022 and the card networks decline RU-issued cards,
  // so a card issued elsewhere is required. We warn but don't block — many
  // RU-located customers successfully pay with foreign-issued cards.
  get showRussianCardWarning(): boolean {
    const country = this.profServ.latestProfile?.locationData?.country;
    const lang = (this.translate.currentLang || "").toLowerCase();
    return country === "Russian Federation" || country === "Russia" || lang.startsWith("ru");
  }

  ngOnInit() {
    this.plan = this.configServ.getPlanByIndex(this.profServ.planIndex);
    // Hydrate from the cached catalog before the first calcCosts so the
    // summary doesn't briefly flash the legacy /config/plans price while we
    // wait on the network. /subscription pre-warms this cache, so on the
    // typical click-through path getCached() returns immediately.
    this.catalog = this.pricingCatalog.getCached();
    this.isCurrencyChange = this.subServ.isCurrencyChange();
    this.calcCosts();
    this.loadCatalog();
    if (this.isCurrencyChange) this.loadConversionPreview();
  }

  /**
   * Cross-currency case: fetch the prorated refund + new charge from the
   * server so the summary can render the cancellation/refund/new-sub lines.
   * Re-runs on interval toggle since the new charge differs monthly vs
   * annual.
   */
  private async loadConversionPreview() {
    const interval = this.profServ.latestProfile.subInterval;
    const result: any = await this.api.previewSubscriptionConversion(
      this.plan.code,
      interval
    );
    if (result && result.data && !this.util.isError(result.data)) {
      this.conversionPreview = result.data as ConversionPreview;
      this.calcCosts();
    }
  }

  /** Currency of the existing (about-to-be-cancelled) subscription, used to
   *  format the refund line. Empty when there's no sub or no currency. */
  get oldSubCurrency(): string {
    const c =
      this.profServ.latestProfile &&
      this.profServ.latestProfile.subscription &&
      this.profServ.latestProfile.subscription.plan &&
      this.profServ.latestProfile.subscription.plan.currency;
    return (c || "").toString().toUpperCase();
  }

  /**
   * Refresh prices from the catalog (handles the cold-cache case and any
   * stale-cache refresh). Failures fall back to whatever `getRate()` already
   * had.
   */
  private async loadCatalog() {
    const fresh = await this.pricingCatalog.get();
    if (fresh) {
      this.catalog = fresh;
      this.calcCosts();
    }
  }
  ngAfterViewInit() {
    if (
      !this.profServ.latestProfile.stripeCards ||
      this.profServ.latestProfile.subscription_card_declined
    )
      this.subServ.card.mount("#card-element");
  }
  getRate(interval: string) {
    // Prefer the catalog (per-country, post-restructure pricing). The catalog
    // is what the user will actually be charged on this upgrade. If the
    // catalog hasn't loaded yet (or the call failed), fall back to the legacy
    // /config/plans entry so the checkout still renders something sensible.
    if (this.catalog && this.plan && this.plan.code) {
      const code = this.plan.code.toString().toLowerCase(); // "home_game" or "club"
      const t = this.catalog.tiers && this.catalog.tiers[code];
      if (t) {
        if (interval === "monthly") return t.monthly;
        if (interval === "annual") return t.annual;
        if (interval === "annual_permonth") return Math.round((t.annual / 12) * 100) / 100;
      }
    }
    const rates = this.plan[this.profServ.latestProfile.currency];
    return rates ? rates[interval] : 0;
  }

  // The currency for display follows the catalog when present; falls back to
  // profile.currency (legacy USD/EUR) otherwise. The HTML template still uses
  // profServ.latestProfile?.currency directly, so this getter is exposed for
  // any future cleanup that needs the resolved currency.
  get displayCurrency(): string {
    if (this.catalog && this.plan && this.plan.code) {
      const code = this.plan.code.toString().toLowerCase();
      const t = this.catalog.tiers && this.catalog.tiers[code];
      if (t && t.currency) return t.currency;
    }
    return (this.profServ.latestProfile && this.profServ.latestProfile.currency) || "USD";
  }
  getCost() {
    return this.getRate(this.profServ.latestProfile.subInterval);
  }
  getCredit() {
    if (this.subServ.latestProration && this.subServ.latestProration.credit)
      return math.round(this.subServ.latestProration.credit / 100, 2);
    else return 0;
  }
  getRemaining() {
    if (this.subServ.latestProration && this.subServ.latestProration.remaining)
      return math.round(this.subServ.latestProration.remaining / 100, 2);
    else return 0;
  }
  getDiscount() {
    if (this.coupon && this.coupon.percent_off) {
      if (
        this.subServ.latestProration &&
        this.subServ.latestProration.credit &&
        this.profServ.latestSubscription &&
        this.profServ.latestSubscription.discount &&
        this.profServ.latestSubscription.discount.coupon &&
        this.profServ.latestSubscription.discount.coupon.id == this.couponID &&
        this.profServ.latestSubscription.discount.coupon.valid
      )
        return 0;
      else {
        let mult = -this.coupon.percent_off / 100;
        return math.round(mult * this.subTotal, 2);
      }
    } else if (this.coupon && this.coupon.amount_off)
      return math.round(-this.coupon.amount_off / 100, 2);
    else return 0;
  }
  calcCosts() {
    // Currency-change path: no Stripe-side proration (the old sub is
    // cancelled, the unused portion is refunded out-of-band in the OLD
    // currency). The amount due is just the new sub's geo price in the new
    // currency. Refund + cancellation render as informational summary lines.
    if (this.isCurrencyChange) {
      this.credit = 0;
      this.remaining = 0;
      this.discount = 0;
      this.subTotal = this.getCost();
      this.tax =
        this.profServ.latestProfile.vat && this.subTotal > 0
          ? math.round(
              (this.profServ.latestProfile.vat / 100) * this.subTotal,
              2
            )
          : 0;
      this.total = math.round(this.subTotal + this.tax, 2);
      this.acctBal = 0;
      this.profServ.amountDue = this.total;
      return;
    }
    if (this.subServ.latestProration && this.subServ.latestProration.credit)
      this.credit = math.round(this.subServ.latestProration.credit / 100, 2);
    if (
      this.subServ.latestProration &&
      this.subServ.latestProration.remaining
    ) {
      this.remaining = math.round(
        this.subServ.latestProration.remaining / 100,
        2
      );
      this.subTotal = this.remaining;
    } else this.subTotal = this.getCost();
    if (this.coupon) this.discount = this.getDiscount();
    if (this.discount)
      this.subTotal = math.round(this.subTotal + this.discount, 2);
    this.subTotal = math.round(this.subTotal + this.credit, 2);
    if (this.profServ.latestProfile.vat && this.subTotal > 0)
      this.tax = math.round(
        (this.profServ.latestProfile.vat / 100) * this.subTotal,
        2
      );
    else this.tax = 0;
    this.total = math.round(this.subTotal + this.tax, 2);
    if (this.profServ.latestProfile.stripeAcctBalance)
      this.acctBal = math.round(
        this.profServ.latestProfile.stripeAcctBalance / 100,
        2
      );
    this.profServ.amountDue = math.round(
      this.subTotal + this.tax + this.acctBal,
      2
    );
  }
  subscribe() {
    if (this.couponID && !this.coupon) this.applyCoupon();
    this.subServ.subscribe(
      this.plan,
      this.profServ.latestProfile.subInterval,
      this.couponID
    );
  }
  checkCoupon() {
    if (
      this.coupon.subscriptionType &&
      this.coupon.subscriptionType != "either"
    ) {
      if (
        this.coupon.subscriptionType != this.profServ.latestProfile.subInterval
      ) {
        this.util.alertDialog("COUPON_INTERVAL_MISMATCH");
        return false;
      }
    } else if (this.coupon.countries) {
      if (
        !this.profServ.latestProfile.locationData ||
        !this.profServ.latestProfile.locationData.country
      ) {
        this.util.alertDialog("COUPON_NO_COUNTRY");
        return false;
      }
      if (
        this.coupon.countries.indexOf(
          this.profServ.latestProfile.locationData.country
        ) != -1
      )
        return true;
      else {
        this.util.alertDialog("COUPON_COUNTRY_MISMATCH");
        return false;
      }
    }
    return true;
  }
  applyCoupon() {
    this.couponID = this.couponID.toUpperCase();
    this.coupon = this.configServ.getCoupon(this.couponID);
    if (this.coupon && this.checkCoupon()) this.calcCosts();
  }
  ngOnDestroy() {
    this.subServ.latestProration = null;
  }
}
